
import { autoinject, computedFrom, observable, transient } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DocumentRequirementModel, SaveDocumentVesselSubscriptionRequest } from "../../../models/document-management.models";
import { LookupClientService } from "@rightship-tech/common-ui/services/lookups/lookup-client.service";
import { LookupValue, LookupValueRequest } from "@rightship-tech/common-ui/models/lookup.models";
import { DocumentRequirementApi } from "../../../apis/document-requirement.api";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { MyVesselApi } from "../../../apis/my-vessels.api";
import { VesselModel } from "../../../models/my-vessels.models";
import { DateTimeHelper } from "@rightship-tech/common-ui/helpers/date-time.helper";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { DocumentManagementDialogConstants } from "../../../constants/dialog.constants";
import * as Enumerable from "linq";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { Claims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { GoogleAnalyticsService } from "@rightship-tech/common-ui/services/analytics/google-analytics.service";

@autoinject
@transient()
export class DialogManageWatchlist {

    private _attachmentTypes: LookupValue[];
    private _formTypes: LookupValue[];
    private _documentRequirementId: number;
    private _savedWatchlistVesselCount: number;
    private _vesselTypeGroup: string;
    public isEditing: boolean;
    public isLoadingVessels: boolean = true;
    public documentRequirement: DocumentRequirementModel;
    public vessels: VesselModel[] = [];
    public filteredVessels: VesselModel[] = [];
    public allVesselsCount: number;
    public isRsUser: boolean;

    @observable
    public selectedAll: boolean;
    
    @observable
    public search: string = "";

    public onSave: () => void;

    constructor(private dialogClientService: DialogClientService, private lookupClientService: LookupClientService,
        private documentRequirementApi: DocumentRequirementApi, private notificationClientService: NotificationClientService,
        private myVesselApi: MyVesselApi, private configService: ConfigService, private userClaimsService: UserClaimsService,
        private googleAnalyticsService: GoogleAnalyticsService) { 
            this.isRsUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        }

    activate(params: { documentRequirementId: number, isEditing: boolean, onSave: () => void }) {   
        this._documentRequirementId = params.documentRequirementId;
        this.isEditing = params.isEditing;
        this.onSave = params.onSave;
    }

    async attached() {

        //Get attachment types lookups
        const requestAttachmentTypes = new LookupValueRequest();
        requestAttachmentTypes.lookupValueQuery = "AttachmentTypes";
        requestAttachmentTypes.parameters = "56,false";
        var attachmentTypes = await this.lookupClientService.getLookup(requestAttachmentTypes)
        this._attachmentTypes = attachmentTypes.activeValues;

        //Get vessel type groups lookups
        const requestForms = new LookupValueRequest();
        requestForms.lookupValueQuery = "OnboardingConfigs"; 
        var forms = await this.lookupClientService.getLookup(requestForms)
        this._formTypes = forms.activeValues;

        //Get document requirement
        this.documentRequirement = await this.documentRequirementApi.getDocumentRequirement(this._documentRequirementId);

        //Get vessels
        const resultVessels = await this.myVesselApi.getMyVessels(this.documentRequirement.vesselTypeGroupId, true, !this.isRsUser);
        this.vessels = Enumerable.from(resultVessels.result).orderBy(x => x.isMonitored ? 1: 2).toArray();
        this.allVesselsCount = resultVessels.count;

        if (this.documentRequirement != null) {
            if (this.documentRequirement.vesselTypeGroupId == null) {
                this._vesselTypeGroup = "All Groups";
            }
            else {
                var requestVesselTypeGroup = new LookupValueRequest("RS_VesselTypeGroups", this.documentRequirement.vesselTypeGroupId.toString());
                var vesselTypeGroup = await this.lookupClientService.getLookup(requestVesselTypeGroup);
                this._vesselTypeGroup = vesselTypeGroup.activeValues.find(x => x.id ==this.documentRequirement.vesselTypeGroupId).name;
            }
        }

        //If editing, get selected vessels
        if (this.isEditing) {
            const selectedVessels = await this.documentRequirementApi.getDocumentRequirementVessels(this._documentRequirementId);
            this.vessels.forEach(x => {
                x.isSelected = selectedVessels.vesselId.includes(x.vesselId);
            });
            this._savedWatchlistVesselCount = selectedVessels.vesselId.length;
        }

        //Search changed
        this.searchChanged();
        this.isLoadingVessels = false;
    }

    //Get selected vessels count
    public get getSelectedVesselsCount() {
        return this.filteredVessels.filter(x => x.isSelected).length;
    }

    // Get vessels' group name
    @computedFrom("_vesselTypeGroup")
    public get vesselGroupName() {
        return this._vesselTypeGroup;
    }

    //Get formatted created date
    @computedFrom("documentRequirement", "documentRequirement.createdDate")
    public get getFormattedCreatedDate() {
        if (this.documentRequirement?.createdDate) {
            return DateTimeHelper.getDateFormatted(this.documentRequirement.createdDate, this.configService.config.common.defaultDateFormat);
        }
        return 'N/a'
    }

    @computedFrom("_isEditing", "getSelectedVesselsCount") 
    public get canSave(): boolean {
        return !this.isEditing && this.getSelectedVesselsCount == 0; 
    }

    //Cancel
    public cancel() {

		//Send event to google analytics
		this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_watchlist_manage_dialog_close_button", {
            "charterer": this.documentRequirement.customerName
        });

        //Hide dialog
        this.dialogClientService.hide();
    }

    //Save
    public async save() {        
        try {
            const request: SaveDocumentVesselSubscriptionRequest = {
                documentRequirementId: this._documentRequirementId,
                vesselSubscriptions: this.vessels.filter(x => x.isSelected).map(x => {
                    return { vesselId: x.vesselId, name: x.vesselName, imo: x.internationalMaritimeOrganizationNumber }
                })
            };
            await this.documentRequirementApi.saveDocumentVesselSubscription(request);
            if(this.onSave) this.onSave();
            this.notificationClientService.show("Watchlist saved successfully", "", NotificationType.Success);

            //Send event to google analytics
            this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_watchlist_manage_dialog_save_button", {
                "charterer": this.documentRequirement.customerName
            });
            
            //Close dialog
            this.dialogClientService.hide();
        }
        catch (error) {
            console.error(error);
            this.notificationClientService.show("An error occurd while trying to save the watchlist", "", NotificationType.Error);
        }
    }

    //Select all
    public selectedAllChanged() {
        this.filteredVessels.forEach(x => x.isSelected = this.selectedAll);
    }

    //Search
    public searchChanged() {
        this.filteredVessels = this.vessels.filter(v => v.vesselName.toLowerCase().includes(this.search.toLowerCase()));
    }

    //Get attachment type name
    public getAttachmentTypeName(id: number): string {
        return this._attachmentTypes.find(x => x.id == id)?.name;
    }

    //Get form name
    public getFormName(id: number): string {
        return this._formTypes.find(x => x.id == id)?.name;
    }

    //Open remove vessels confirmation dialog
    public openRemoveVesselsConfirmationDailog() {
        this.dialogClientService.show(DocumentManagementDialogConstants.DeleteWatchlistVesselsConfirmationDialog,
            {
                documentRequirementName: this.documentRequirement?.name ?? "N/a",
                documentRequirementId: this.documentRequirement?.documentRequirementId,
                selectedVesselCount: this._savedWatchlistVesselCount,
                totalVesselCount: this.allVesselsCount,
                onDelete: (() => {
                    if(this.onSave) this.onSave();
                    this.dialogClientService.hide();
                }).bind(this)
            }, true);
    }
}
